const DEFAULT_PRIORITY = Number.MAX_SAFE_INTEGER

/**
 * Parse priority from image URL hash fragment.
 * URLs can contain #priority=N where N is a number.
 * Lower numbers = higher priority.
 * Returns DEFAULT_PRIORITY if no priority found.
 */
function getImagePriority(url: string | null | undefined): number {
  if (!url) return DEFAULT_PRIORITY
  const match = url.match(/#priority=(\d+)/)
  if (match?.[1]) {
    return parseInt(match[1], 10)
  }
  return DEFAULT_PRIORITY
}

/**
 * Select the best image URL from a list based on priority.
 * Priority is parsed from #priority=N hash fragment in URL.
 * Falls back to first non-null URL if no priorities exist.
 */
function selectBestImageUrl(urls: (string | null | undefined)[]): string | null {
  const validUrls = urls.filter((url): url is string => !!url)
  if (validUrls.length === 0) return null

  const sorted = [...validUrls].sort((a, b) => getImagePriority(a) - getImagePriority(b))
  return sorted[0] ?? null
}

/**
 * Select the best image URL from objects that have an imageUrl property.
 * Useful for selecting from variants, entries, etc.
 * Accepts unknown[] for flexibility with ag-grid data.
 */
export function selectBestImageUrlFromObjects(
  objects: unknown[] | null | undefined,
): string | null {
  if (!objects || objects.length === 0) return null
  const urls = objects.map((obj) => {
    if (obj && typeof obj === "object" && "imageUrl" in obj) {
      const url = (obj as { imageUrl?: unknown }).imageUrl
      return typeof url === "string" ? url : null
    }
    return null
  })
  return selectBestImageUrl(urls)
}
